/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { getDay, addDays, subWeeks, format, isLastDayOfMonth, addWeeks, isWeekend, isSameMonth } from 'date-fns';
import { Weekday } from 'rrule';
import { PeriodOptions, ordinal } from './utils';
import { Rule, RuleTypes, getRule } from './rules';

/**
 * Interface for classes that represents a suggestion.
 * @interface
 */
export interface Suggestion {
  label: string;
  value: string;
  rule?: Rule;
}

/**
 * Enum for suggestion type values.
 * @readonly
 * @enum {string}
 */
export enum SuggestionType {
  'Day' = 'DAY', // on Friday
  'Date' = 'DATE', // on the 12th
  'NWeekDay' = 'N_WEEK_DAY', // on the 3rd (or last) Friday
  'LastDay' = 'LAST_DAY', // on the last day
  'LastWorkDay' = 'LAST_WORK_DAY', // on the last workday
}

/**
 * Creates a new Suggestion for the specified suggestion type and period.
 * @class
 */
export class SuggestionFactory {
  date: Date;
  isLastDay: boolean;
  isWeekendDay: boolean;
  stringDay: string;
  day: string;
  dayNumber: number;
  month: string;
  dayOfTheWeek: number;
  weekday: any;
  byMonth: any;

  /**
   * @constructor
   * @param {Date} date
   */
  constructor(date: Date) {
    this.date = date;
    this.isLastDay = isLastDayOfMonth(date);
    this.isWeekendDay = isWeekend(date);
    this.stringDay = format(date, 'EEEE'); // Monday, Tuesday, ..., Sunday
    this.day = format(date, 'do'); // 1st, 2nd, ..., 31st
    this.dayNumber = parseInt(format(date, 'd')); // 1, 2, ..., 31
    this.month = format(date, 'MMMM'); // January, February, ..., December
    this.dayOfTheWeek = getDay(date); // 0 | 1 | 2 | 3 | 4 | 5 | 6
    this.weekday = new Weekday(this.dayOfTheWeek - 1);
    this.byMonth = parseInt(format(date, 'L')); // 1, 2, 3, ..., 12
  }

  /**
   * Creates a suggestion for the specified suggestion type and period.
   * If the suggestion type does not exist or if the chosen date does not have suggestions it returns undefined
   * @param {string} period WEEKLY | MONTHLY | QUARTERLY | SEMIANNUALLY | ANNUALLY/YEARLY
   * @param {SuggestionType} suggestionType
   */
  public getSuggestion(period: string, suggestionType: SuggestionType): Suggestion | undefined {
    switch (suggestionType) {
      case SuggestionType.Day:
        return createDaySuggestion(period, this);

      case SuggestionType.Date:
        return createDateSuggestion(period, this);

      case SuggestionType.LastDay:
        return createLastDaySuggestion(period, this);

      case SuggestionType.NWeekDay:
        return createWeekDaySuggestion(period, this);

      case SuggestionType.LastWorkDay:
        return createLastWorkDaySuggestion(period, this);

      default:
        return;
    }
  }
}

/**
 * Creates a day suggestion object
 * It covers the following cases:
 *    - due weekly on Friday
 * @param {string} period WEEKLY | MONTHLY | QUARTERLY | SEMIANNUALLY | ANNUALLY/YEARLY
 * @param {Object} attributes Contains date attributes which the suggestions are based on
 * @returns {Suggestion} Object that contains the suggestion description and the related RRule
 */
function createDaySuggestion(period: string, attributes: any) {
  let due = `${attributes.stringDay}`;
  return {
    label: due,
    value: due,
    rule: getRule(period, RuleTypes.Day, attributes),
  };
}

/**
 * Creates a date suggestion object
 * It covers the following cases:
 *    - due monthly on the 12th
 *    - due on the 5th of [the month / July]
 * @param {string} period WEEKLY | MONTHLY | QUARTERLY | SEMIANNUALLY | ANNUALLY/YEARLY
 * @param {Object} attributes Contains date attributes which the suggestions are based on
 * @returns {Suggestion} Object that contains the suggestion description and the related RRule
 */
function createDateSuggestion(period: string, attributes: any) {
  let due = '';
  if (period === PeriodOptions.Monthly) {
    // e.g. due monthly on the 12th
    due = `the ${attributes.day}`;
  } else {
    // e.g. due on the 5th of [the month / July]
    let end = 'of the month';
    if (period === PeriodOptions.Yearly || period === PeriodOptions.Annually) {
      end = `of ${attributes.month}`;
    }

    due = `the ${attributes.day} ${end}`;
  }

  return {
    label: due,
    value: due,
    rule: getRule(period, RuleTypes.Date, attributes),
  };
}

/**
 * Creates a lastday suggestion object
 * It covers the following cases:
 *    - due on the last day of [the month / July]
 * @param {string} period WEEKLY | MONTHLY | QUARTERLY | SEMIANNUALLY | ANNUALLY/YEARLY
 * @param {Object} attributes Contains date attributes which the suggestions are based on
 * @returns {Suggestion|undefined} It returns an object that contains the suggestion description and the
 *  related RRule or undefined if is not the last day
 */
function createLastDaySuggestion(period: string, attributes: any) {
  if (attributes.isLastDay) {
    // e.g. due on the last day of [the month / July]
    let end = 'of the month';
    if (period === PeriodOptions.Yearly || period === PeriodOptions.Annually) {
      end = `of ${attributes.month}`;
    }

    const due = `the last day ${end}`;
    const rule = getRule(period, RuleTypes.LastDay, attributes);

    return {
      label: due,
      value: due,
      rule,
    };
  }
}

/**
 * Creates a weekday suggestion object
 * It covers the following cases:
 *    - due {period} on the last Friday of [the month / July]
 *    - due {period} on the 3rd xday of [the month / July]
 * @param {string} period WEEKLY | MONTHLY | QUARTERLY | SEMIANNUALLY | ANNUALLY/YEARLY
 * @param {Object} attributes Contains date attributes which the suggestions are based on
 * @returns {Suggestion} Object that contains the suggestion description and the related RRule
 */
function createWeekDaySuggestion(period: string, attributes: any): Suggestion {
  let due;
  let rule;
  let end = 'of the month';
  if (period === PeriodOptions.Yearly || period === PeriodOptions.Annually) {
    end = `of ${attributes.month}`;
  }

  if (!isSameMonth(attributes.date, addWeeks(attributes.date, 1))) {
    // e.g. due on the last Friday of [the month / July]
    due = `the last ${attributes.stringDay} ${end}`;
    rule = getRule(period, RuleTypes.NWeekDay, {
      weekday: attributes.weekday,
      weekNumber: -1,
      byMonth: attributes.byMonth,
    });
  } else {
    // e.g. due on the 3rd xday of [the month / July]
    let weekNumber = 0;
    let checkDate = attributes.date;
    while (isSameMonth(attributes.date, checkDate)) {
      checkDate = subWeeks(checkDate, 1);
      weekNumber++;
    }
    due = `the ${ordinal(weekNumber)} ${attributes.stringDay} ${end}`;
    rule = getRule(period, RuleTypes.NWeekDay, {
      weekday: attributes.weekday,
      weekNumber,
      byMonth: attributes.byMonth,
    });
  }
  return {
    label: due,
    value: due,
    rule,
  };
}

/**
 * Creates a last workday suggestion object - We consider workday from monday to friday
 * It covers the following cases:
 *    - on the last weekday of [the month / July]
 * @param {string} period WEEKLY | MONTHLY | QUARTERLY | SEMIANNUALLY | ANNUALLY/YEARLY
 * @param {Object} attributes Contains date attributes which the suggestions are based on
 * @returns {Suggestion|undefined} If is a workday it return an object that contains the suggestion description
 *  and the related RRule
 */
function createLastWorkDaySuggestion(period: string, attributes: any) {
  // e.g. on the last weekday of [the month / July]
  if (!attributes.isWeekendDay) {
    let end = 'of the month';
    if (period === PeriodOptions.Yearly || period === PeriodOptions.Annually) {
      end = `of ${attributes.month}`;
    }

    let addDates = attributes.dayOfTheWeek === 5 ? 3 : 1;
    const nextWeekDay = addDays(attributes.date, addDates);
    if (!isSameMonth(attributes.date, nextWeekDay)) {
      const due = `the last weekday ${end}`;
      const rule = getRule(period, RuleTypes.LastWorkDay, attributes);
      return {
        label: due,
        value: due,
        rule,
      };
    }
  }
}
